import { extractApiError } from 'utils';
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  changePasswordRequest,
  getMeRequest,
  logInRequest,
  LoginRequestType,
  restorePasswordRequest,
  signUpRequest,
  SignUpRequestType,
  verifyResetRequest,
} from 'api';
import { LsKeys, routes } from 'enums';
import { authActions } from 'state';

/**
 * What the sign-in form shows instead of the API's own wording.
 *
 * ONE MESSAGE FOR BOTH FAILURE MODES, DELIBERATELY. It is tempting to answer
 * "no account with that number" and "wrong password" separately, because that
 * is more helpful. It also turns the login form into a tool for discovering
 * which phone numbers have accounts: submit numbers with a junk password and
 * read the difference in the replies. So every wrong-credentials outcome gets
 * the same sentence, and that sentence names both fields.
 *
 * The API already behaves this way — it answers FORBIDDEN whether the account
 * is missing or the password is wrong — so this only stops the UI from
 * reintroducing a distinction the server was careful not to make.
 */
const LOGIN_ERRORS: Record<string, string> = {
  // Wrong phone, wrong password, or an account that is not an administrator.
  FORBIDDEN: 'The phone number or password is incorrect. Please try again.',
  // A guard rejected the request before the credential check ran.
  UNAUTHORIZED: 'The phone number or password is incorrect. Please try again.',
  // The auth limiter allows 10 attempts per 15 minutes, keyed on the phone
  // number as well as the IP — so "wait and retry" is genuinely actionable.
  TOO_MANY_REQUESTS:
    'Too many sign-in attempts. Please wait about 15 minutes and try again.',
};

export const login = createAsyncThunk(
  'auth/login',
  async (
    { user }: { user: LoginRequestType },
    { rejectWithValue, getState, dispatch },
  ) => {
    try {
      const res = await logInRequest(user);
      console.log(res.data);
      console.log('----check role-------->');

      console.log('login success');
      return res.data;
    } catch (error: any) {
      console.log('login failed');
      console.log(error);
      return rejectWithValue(extractApiError(error, LOGIN_ERRORS));
    } finally {
      //thunkAPI.dispatch(authActions.setLoading({ isLoading: false }));
    }
  },
);

export const getMe = createAsyncThunk(
  'auth/me',
  async (_, { rejectWithValue, getState, dispatch }) => {
    try {
      const res = await getMeRequest();
      console.log('navigate');
      // navigation.navigate(SCREENS.PROFILE);
      return res.data;
    } catch (error: any) {
      console.log('error get me');
      console.log(error);
      return rejectWithValue(extractApiError(error));
    } finally {
      console.log('finally');
      //SplashScreen.hide();
      //SplashScreen.hide();

      // thunkAPI.dispatch(authActions.setInitialized());
      // thunkAPI.dispatch(authActions.setLoading({ isLoading: false }));
    }
  },
);

export const restorePassword = createAsyncThunk(
  'auth/restore-password',
  async (
    { restoreData }: { restoreData: any },
    { rejectWithValue, getState, dispatch },
  ) => {
    try {
      const res = await restorePasswordRequest(restoreData);

      return res.data;
    } catch (error: any) {
      return rejectWithValue(extractApiError(error));
    } finally {
      //thunkAPI.dispatch(authActions.setLoading({ isLoading: false }));
    }
  },
);

export const verifyReset = createAsyncThunk(
  'auth/verify-reset',
  async (
    { verifyData, navigate }: { verifyData: any; navigate: any },
    { rejectWithValue, getState, dispatch },
  ) => {
    try {
      console.log('verify reset');
      const res = await verifyResetRequest(verifyData);
      navigate(routes.AUTH.CHANGE_PASSWORD);

      return res.data;
    } catch (error: any) {
      return rejectWithValue(extractApiError(error));
    } finally {
      console.log('finally');
      //thunkAPI.dispatch(authActions.setLoading({ isLoading: false }));
    }
  },
);

export const changePassword = createAsyncThunk(
  'auth/change-password',
  async (
    { changeData, navigate }: { changeData: any; navigate: any },
    { rejectWithValue },
  ) => {
    try {
      const res = await changePasswordRequest(changeData);
      navigate(routes.AUTH.LOG_IN);

      return res.data;
    } catch (error: any) {
      // The catch used to log and fall through, so the thunk FULFILLED with
      // undefined on failure: no rejected case ran, no error reached the
      // store, and the form sat there having silently done nothing. An admin
      // whose reset token had expired got no indication at all.
      return rejectWithValue(extractApiError(error));
    }
  },
);

export const authThunk = { login, getMe, restorePassword, verifyReset, changePassword };
